import styles from './Form.module.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import CustomDropdown from '../../ui/CustomDropdown/CustomDropdown';
import CustomInputDropdown from '../../ui/CustomInputDropdown/CustomInputDropdown';
import CustomPhoneInputField from './CustomPhoneInputField/CustomPhoneInputField';
import cn from 'classnames';
import rightArrow from '../../../../../src/assets/icons/right-arrow.svg';
import { Link, RichText } from '@sitecore-jss/sitecore-jss-react';
import { color } from 'framer-motion';

const buildOptions = (fields) => {
  return fields.map((item) => {
    return {
      label: item.fields.label.value,
      value: item.fields.value.value,
      postCode: item.fields.postCode ? item.fields.postCode.value : '',
      // 兼容 Sitecore 新增的布尔字段 `check`（或命名为 boolValue）
      boolValue:
        typeof item.fields?.check?.value === 'boolean'
          ? item.fields.check.value
          : typeof item.fields?.boolValue?.value === 'boolean'
          ? item.fields.boolValue.value
          : undefined,
      brand: item.fields?.brand?.value,
      // 添加 versionList 和 colourList 支持
      versionList: item.fields?.versionList || [],
      colourList: item.fields?.colourList || [],
    };
  });
};

export default function ContactUsForm({
  fields,
  isSubmitting,
  emitChangeSome,
  retailerOptions = [],
  onRetailerSearch,
}) {
  const {
    register,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    trigger,
  } = useFormContext();

  const [autoSelectedModel, setAutoSelectedModel] = useState(false);
  const [selectedModelOption, setSelectedModelOption] = useState(null);
  const [selectedVersionOption, setSelectedVersionOption] = useState(null);
  const [selectedColorOption, setSelectedColorOption] = useState(null);
  const [autoSelectedDealer, setAutoSelectedDealer] = useState(false); // 新增：经销商自动选择状态
  const [selectedDealerOption, setSelectedDealerOption] = useState(null); // 新增：选中的经销商选项
  const [autoSelectedVersion, setAutoSelectedVersion] = useState(false); // 新增：版本自动选择状态
  const [autoSelectedColor, setAutoSelectedColor] = useState(false); // 新增：颜色自动选择状态

  const modelOptions = useMemo(() => buildOptions(fields.modelOption), [fields.modelOption]);
  const interestedOption = useMemo(
    () => buildOptions(fields.interestedOption),
    [fields.interestedOption]
  );

  // 获取当前选中车型的版本选项
  const getVersionOptions = () => {
    if (!selectedModelOption) return [];

    // 从 modelOptions 中找到对应的车型数据
    const carData = modelOptions.find(
      (option) =>
        option.label === selectedModelOption.label && option.value === selectedModelOption.value
    );

    if (carData?.versionList && carData.versionList.length > 0) {
      return carData.versionList.map((version) => ({
        label: version.fields?.label?.value || version.fields?.value?.value || '',
        value: version.fields?.value?.value || version.fields?.label?.value || '',
        id: version.id,
        price: version.fields?.price?.value || 0,
      }));
    }

    return []; // 如果没有版本数据，返回空数组
  };

  // 获取当前选中车型的颜色选项（支持版本与颜色联动）
  const getColorOptions = () => {
    if (!selectedModelOption) return [];

    // 从 modelOptions 中找到对应的车型数据
    const carData = modelOptions.find(
      (option) =>
        option.label === selectedModelOption.label && option.value === selectedModelOption.value
    );

    if (!carData) return [];

    const baseColours = carData.colourList || [];

    // 如果选中了版本，优先根据版本的 childrenList 过滤颜色
    if (selectedVersionOption && Array.isArray(carData.versionList)) {
      const matchedVersion = carData.versionList.find(
        (version) =>
          // 优先通过 id 精确匹配，避免同名/多语言差异
          version.id === selectedVersionOption.id ||
          version.fields?.label?.value === selectedVersionOption.label ||
          version.fields?.value?.value === selectedVersionOption.value
      );

      const childrenColours = matchedVersion?.fields?.childrenList || [];

      if (childrenColours.length > 0) {
        // childrenList 本身就包含 label / value 信息，直接用它们生成颜色选项
        return childrenColours.map((color) => ({
          label: color.fields?.label?.value || color.fields?.value?.value || '',
          value: color.fields?.value?.value || color.fields?.label?.value || '',
          id: color.id,
          price: color.fields?.price?.value || 0,
          image: color.fields?.image?.value,
        }));
      }
    }

    // 没有选中版本，或者版本下没有特定颜色时，退回到车型自身的颜色列表
    if (baseColours.length > 0) {
      return baseColours.map((color) => ({
        label: color.fields?.label?.value || color.fields?.value?.value || '',
        value: color.fields?.value?.value || color.fields?.label?.value || '',
        id: color.id,
        price: color.fields?.price?.value || 0,
        image: color.fields?.image?.value,
      }));
    }

    return []; // 如果没有颜色数据，返回空数组
  };

  const versionOptions = getVersionOptions();
  const colorOptions = getColorOptions();

  // 调试信息
  console.log('ContactUs Debug:', {
    selectedModelOption,
    modelOptionsLength: modelOptions.length,
    versionOptionsLength: versionOptions.length,
    colorOptionsLength: colorOptions.length,
    modelOptionsWithData: modelOptions
      .filter((option) => option.versionList.length > 0 || option.colourList.length > 0)
      .map((option) => ({
        label: option.label,
        versionsCount: option.versionList.length,
        colorsCount: option.colourList.length,
      })),
  });

  // 检测URL参数并自动选择车型
  useEffect(() => {
    if (autoSelectedModel || !modelOptions.length) return;

    const urlParams = new URLSearchParams(window.location.search);
    const carModel = urlParams.get('carModel');

    if (carModel && modelOptions.length > 0) {
      // 查找匹配的车型选项
      const matchedOption = modelOptions.find(
        (option) => option.label.toLowerCase() === carModel.toLowerCase()
      );

      if (matchedOption) {
        setSelectedModelOption(matchedOption);
        setAutoSelectedModel(true);
        // 设置表单值
        console.log(matchedOption, 'matchedOption');
        setValue('carModelCode', matchedOption.value);
        setValue('brand', matchedOption.brand);
        // 延迟触发验证
        setTimeout(() => {
          trigger('carModelCode');
        }, 100);
      } else {
      }
    }
  }, [modelOptions, setValue, trigger, autoSelectedModel]);

  // 检测URL参数并自动选择经销商
  useEffect(() => {
    if (autoSelectedDealer || !retailerOptions.length) return;

    const urlParams = new URLSearchParams(window.location.search);
    const dealer = urlParams.get('dealer');

    if (dealer && retailerOptions.length > 0) {
      // 查找匹配的经销商选项
      const matchedDealer = retailerOptions.find(
        (option) => option.value === dealer
        // (option) => option.label.toLowerCase() === dealer.toLowerCase()
      );
      if (matchedDealer) {
        setSelectedDealerOption(matchedDealer);
        setAutoSelectedDealer(true);

        // 设置表单值
        setValue('storeCode', matchedDealer.value);

        // 延迟触发验证
        setTimeout(() => {
          trigger('storeCode');
        }, 100);
      }
    }
  }, [retailerOptions, setValue, trigger, autoSelectedDealer]);

  // 新增：检测URL参数并自动选择版本
  useEffect(() => {
    if (autoSelectedVersion || !selectedModelOption) return;

    const urlParams = new URLSearchParams(window.location.search);
    const carVersion = urlParams.get('carVersion');

    if (carVersion) {
      const versionOptions = getVersionOptions();

      if (versionOptions.length > 0) {
        // 查找匹配的版本选项
        const matchedVersion = versionOptions.find(
          (option) => option.value.toLowerCase() === carVersion.toLowerCase()
        );

        if (matchedVersion) {
          setSelectedVersionOption(matchedVersion);
          setAutoSelectedVersion(true);

          // 设置表单值
          setValue('carVersion', matchedVersion.value);

          // 延迟触发验证
          setTimeout(() => {
            trigger('carVersion');
          }, 100);
        }
      }
    }
  }, [selectedModelOption, setValue, trigger, autoSelectedVersion, getVersionOptions]);

  // 新增：检测URL参数并自动选择颜色
  useEffect(() => {
    if (autoSelectedColor || !selectedModelOption) return;

    const urlParams = new URLSearchParams(window.location.search);
    const carColor = urlParams.get('carColor');

    if (carColor) {
      const colorOptions = getColorOptions();

      if (colorOptions.length > 0) {
        // 查找匹配的颜色选项
        const matchedColor = colorOptions.find(
          (option) => option.label.toLowerCase() === carColor.toLowerCase()
        );

        if (matchedColor) {
          setSelectedColorOption(matchedColor);
          setAutoSelectedColor(true);

          // 设置表单值
          setValue('carColor', matchedColor.value);

          // 延迟触发验证
          setTimeout(() => {
            trigger('carColor');
          }, 100);
        }
      }
    }
  }, [
    selectedModelOption,
    selectedVersionOption,
    setValue,
    trigger,
    autoSelectedColor,
    getColorOptions,
  ]);

  const handleSelect = async (option, field) => {
    setValue(field, option.value);

    // 如果是车型选择，更新状态并重置版本和颜色
    if (field === 'carModelCode') {
      setValue('brand', option.brand);
      setSelectedModelOption(option);

      // 重置版本和颜色选择（因为换了车型）
      setSelectedVersionOption(null);
      setSelectedColorOption(null);
      setValue('carVersion', '');
      setValue('carColor', '');

      // 重置自动选择状态
      setAutoSelectedVersion(false);
      setAutoSelectedColor(false);

      // 清除版本和颜色的验证错误
      clearErrors('carVersion');
      clearErrors('carColor');

      emitChangeSome(option);
    }

    // 如果是版本选择，更新状态，并重置颜色选择（因为不同版本允许的颜色可能不同）
    if (field === 'carVersion') {
      setSelectedVersionOption(option);
      setSelectedColorOption(null);
      setValue('carColor', '');
      clearErrors('carColor');
    }

    // 如果是颜色选择，更新状态（不影响版本选择）
    if (field === 'carColor') {
      setSelectedColorOption(option);
    }

    // 如果是经销商选择，更新状态
    if (field === 'storeCode') {
      setSelectedDealerOption(option);
    }

    await trigger(field);
  };
  // 获取URL参数中的type值
  const urlParams = new URLSearchParams(window.location.search);
  const contactType = urlParams.get('type');
  return (
    <>
      <div className={styles.fields}>
        <div className={cn(styles.field, styles.field_postcode)}>
          <label className={styles.field_label}>{fields.carModelTitle.value}</label>
          <div className={styles.field_dropdown}>
            <CustomDropdown
              options={modelOptions}
              placeholderValue={
                <span className={styles.field_dropdown_placeholder}>
                  {fields?.modelPlaceholder?.value}
                </span>
              }
              selectedValue={selectedModelOption}
              isOptionsAbsolutePosition
              onSelect={(option) => handleSelect(option, 'carModelCode')}
            />
          </div>
          {errors.carModelCode && (
            <span className={styles.field_error} style={{ bottom: -15 }}>
              {String(errors.carModelCode.message)}
            </span>
          )}
        </div>

        {/* 版本和颜色选择 - 始终显示在同一行 */}
        {contactType !== 'testDrive' && selectedModelOption && (
          <div className={styles.field_row}>
            {/* 版本选择 - 始终显示 */}
            <div className={cn(styles.field, styles.field_half)}>
              <label className={styles.field_label}>
                {fields.versionTitle?.value || 'Select Version'}
              </label>
              <div className={styles.field_dropdown}>
                <CustomDropdown
                  key={`version-${selectedModelOption?.value || 'none'}`}
                  options={versionOptions}
                  placeholderValue={
                    <span className={styles.field_dropdown_placeholder}>
                      {fields?.versionPlaceholder?.value || 'Choose a version'}
                    </span>
                  }
                  selectedValue={selectedVersionOption}
                  isOptionsAbsolutePosition
                  onSelect={(option) => handleSelect(option, 'carVersion')}
                />
              </div>
              {errors.carVersion && (
                <span className={styles.field_error} style={{ bottom: -15 }}>
                  {String(errors.carVersion.message)}
                </span>
              )}
            </div>

            {/* 颜色选择 - 始终显示 */}
            <div className={cn(styles.field, styles.field_half)}>
              <label className={styles.field_label}>
                {fields.colorTitle?.value || 'Select Color'}
              </label>
              <div className={styles.field_dropdown}>
                <CustomDropdown
                  key={`color-${selectedModelOption?.value || 'none'}`}
                  options={colorOptions}
                  placeholderValue={
                    <span className={styles.field_dropdown_placeholder}>
                      {fields?.colorPlaceholder?.value || 'Choose a color'}
                    </span>
                  }
                  selectedValue={selectedColorOption}
                  isOptionsAbsolutePosition
                  onSelect={(option) => handleSelect(option, 'carColor')}
                />
              </div>
              {errors.carColor && (
                <span className={styles.field_error} style={{ bottom: -15 }}>
                  {String(errors.carColor.message)}
                </span>
              )}
            </div>
          </div>
        )}
        <div className={cn(styles.field, styles.field_postcode)}>
          <label className={styles.field_label}>{fields.intestedTitle.value}</label>
          <div className={styles.field_dropdown}>
            <CustomDropdown
              options={interestedOption}
              placeholderValue={
                <span className={styles.field_dropdown_placeholder}>
                  {fields?.testDrivePlaceholder?.value}
                </span>
              }
              isOptionsAbsolutePosition
              onSelect={(option) => {
                // 优先使用 Sitecore 布尔字段；其次直接用 value 为布尔；否则从字符串 yes/no 推断
                const bool =
                  typeof option.boolValue === 'boolean'
                    ? option.boolValue
                    : typeof option.value === 'boolean'
                    ? option.value
                    : String(option.value).toLowerCase() === 'yes';
                handleSelect({ value: bool }, 'interested');
              }}
            />
          </div>
          {errors.interested && (
            <span className={styles.field_error} style={{ bottom: -15 }}>
              {String(errors.interested.message)}
            </span>
          )}
        </div>
        <div className={styles.field}>
          <label className={styles.field_label}>{fields.firstNameTitle.value}</label>
          <input
            className={styles.field_input}
            type="text"
            placeholder={fields?.firstNamePlaceholder?.value}
            {...register('firstName')}
          />
          {errors.firstName && (
            <span className={styles.field_error}>{String(errors.firstName.message)}</span>
          )}
        </div>
        <div className={styles.field}>
          <label className={styles.field_label}>{fields.lastNameTitle.value}</label>
          <input
            className={styles.field_input}
            type="text"
            placeholder={fields?.lastNamePlaceholder?.value}
            {...register('lastName')}
          />
          {errors.lastName && (
            <span className={styles.field_error}>{String(errors.lastName.message)}</span>
          )}
        </div>
        <div className={styles.field}>
          <label className={styles.field_label}>{fields.phoneNumberTitle.value}</label>
          <CustomPhoneInputField
            control={control}
            placeholder={fields?.phonePlaceholder?.value}
            name="phoneNumber"
            trigger={trigger}
          />
          {errors.phoneNumber && (
            <span className={styles.field_error}>{String(errors.phoneNumber.message)}</span>
          )}
        </div>
        <div className={styles.field}>
          <label className={styles.field_label}>{fields.emailTitle.value}</label>
          <input
            className={styles.field_input}
            type="text"
            placeholder={fields?.emailPlaceholder?.value}
            {...register('email')}
            onChange={(e) => {
              const jsonData = {
                id: 'c611a07e-2445-4fa1-b83f-3dac6b74ef83',
                revisionNumber: '1',
                formUrl: 'http://localhost:8000/en/maintenance',
                effectiveFromDate: '2024-04-23 17:08:58',
              };
              setValue('termsAndConditions', JSON.stringify(jsonData));
            }}
          />
          {errors.email && (
            <span className={styles.field_error}>{String(errors.email.message)}</span>
          )}
        </div>
        <div className={cn(styles.field, styles.field_postcode)}>
          <label className={styles.field_label}>{fields.selectRetailerTitle.value}</label>
          <div className={`${styles.field_dropdown}`}>
            <CustomInputDropdown
              options={retailerOptions}
              placeholderValue={fields?.retailerPlaceholder?.value}
              searchEmpty={fields?.searchEmpty?.value}
              selectedValue={selectedDealerOption} // 显示选中的经销商
              isOptionsAbsolutePosition
              onSelect={(option) => handleSelect(option, 'storeCode')}
              onSearchChange={onRetailerSearch}
              disabled={autoSelectedDealer}
            />
          </div>
          {errors.storeCode && (
            <span className={styles.field_error} style={{ bottom: -15 }}>
              {String(errors.storeCode.message)}
            </span>
          )}
        </div>
      </div>
      <div className={styles.field}>
        <label className={styles.field_label}>{fields.sendUsMessageTitle.value}</label>
        <textarea className={styles.field_textarea} {...register('feedback')} />
      </div>
      <div className={styles.checkboxContainer}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            {...register('termsAndConditionsCheck')}
            className={styles.checkboxInput}
            onChange={(e) => {
              const jsonData = {
                revisionNumber: fields.termsAndCondition?.fields.revisionNumber.value,
                effectiveFromDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
                termsAndConditionsCheck: e.target.checked,
                id: fields.termsAndCondition?.fields.id,
                formUrl: window.location.href,
              };
              setValue('termsAndConditions', jsonData);
              if (e.target.checked) {
                clearErrors('termsAndConditionsCheck');
              }
            }}
          />
          <span className={styles.customCheckbox}></span>
          <div className={styles.checkboxTextContainer}>
            {!errors.termsAndConditionsCheck && <RichText field={fields?.policyRichText} />}
            {errors.termsAndConditionsCheck && <RichText field={fields?.agreeMsgRichText} />}

            {/* {!errors.termsAndConditionsCheck && <span>{fields.acceptPreTitle.value}</span>} */}
            {/* {errors.termsAndConditionsCheck && ( */}
            {/*   <span style={{ color: 'rgba(227, 99, 108, 1)' }}>{fields.agreeMsg.value}</span> */}
            {/* )} */}
            {/* <Link */}
            {/*   style={errors.termsAndConditionsCheck ? { color: 'rgba(227, 99, 108, 1)' } : {}} */}
            {/*   field={fields.privacyPolicyUrl} */}
            {/* > */}
            {/*   {fields.acceptSurTitle.value} */}
            {/* </Link> */}
          </div>
        </label>
      </div>

      <div className={styles.checkboxContainer}>
        <label className={(styles.checkboxLabel, styles.flex_start)}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            onChange={(e) => {
              setValue('Subcription', !!e.target.checked);
            }}
          />
          <span className={styles.customCheckbox}></span>
          <span className={styles.checkboxLabel_text}>
            {fields?.messageSubscriptionText?.value}
          </span>
        </label>
      </div>
      <button
        type="submit"
        className={cn(styles.submitButton, { [styles.disabled]: isSubmitting })}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Submitting...' : fields.submitTitle.value}
        <img src={rightArrow} alt="arrow" />
      </button>
    </>
  );
}
